import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Icon,
  Input,
  Select,
  Spinner,
  Text,
  Tooltip,
  useColorModeValue,
} from "@chakra-ui/react";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import {
  MdCheckCircle,
  MdDelete,
  MdEdit,
  MdInfoOutline,
  MdPause,
  MdPlayArrow,
  MdUploadFile,
} from "react-icons/md";
import GamelightButton from "components/button";
import { useConfirmDialog } from "components/confirmModal";
import {
  OptimizeGame,
  OptimizeCardDef,
  cooldownError,
  cooldownPeriodLabel,
  maxChallengesError,
} from "../data";
import {
  ActivationRefusal,
  BoardCard,
  BoardEntry,
  CREATIVE_LANGUAGE_CODES,
  OptimizationKey,
  OptimizeApiError,
  OptimizeApp,
  OptimizeBoard,
  Platform,
  TimeLimitedParams,
  deleteCreative,
  uploadCreative,
  validateCreativeFile,
} from "../api";
import { parseGameId, useCreatives, useOptimizeLive } from "../liveStore";

const utc = (iso: string) => moment.utc(iso).format("D MMM YYYY HH:mm:ss");

// refusal codes that mean "not configured yet" — shown as a disabled-button
// tooltip, not as a red error
const SETUP_REFUSALS = ["event_name_required", "params_required"];

// Günay's copy for the eligibility flag: the backend refuses purchase cards on
// low-IAP-share games with a code the switch below doesn't know, so the reason
// has to come from /apps' eligibility.purchaseChallenges instead
const PURCHASE_INELIGIBLE_TEXT =
  "This challenge is available for games where IAP monetization share is higher than 50%.";

// An ineligible title can't use the purchase cards at all — the whole setup
// panel (event input included) locks, not just the Activate button. Applies to
// both purchase cards (Günay confirmed multi-game follows the same IAP rule).
const purchaseIneligible = (
  key: OptimizationKey,
  app: OptimizeApp | null | undefined
) =>
  (key === "purchase_challenges" ||
    key === "multi_game_purchase_challenges") &&
  app?.eligibility?.purchaseChallenges === false;

// numbers come from the refusal itself, falling back to the board's rules —
// never from a frontend constant
const refusalMessage = (
  refusal: ActivationRefusal,
  rules?: OptimizeBoard["rules"],
  fallback?: string
): string => {
  switch (refusal.code) {
    case "max_active_challenges_reached":
      return maxChallengesError(
        refusal.maxActive ?? rules?.maxActiveChallengesPerApp
      );
    case "activation_cooldown":
      return cooldownError(
        `${utc(refusal.lastActivatedAt!)} UTC`,
        `${utc(refusal.nextAvailableAt!)} UTC`,
        rules?.activationCooldownHours
      );
    case "iap_share_too_low":
      // iapSharePercent is null for titles with no revenue split on record
      return [
        `Purchase challenges are available for games where IAP monetization share is higher than ${
          refusal.requiredIapSharePercent ?? 50
        }%.`,
        refusal.iapSharePercent != null
          ? `This game is currently at ${refusal.iapSharePercent}%.`
          : "We don't have an IAP share on record for this game yet.",
      ].join(" ");
    case "iap_share_unknown":
      // distinct from too-low per CONTRACT.md: there is no share on record,
      // and recording one is on us, not something the partner can play toward
      return (
        `Purchase challenges are available for games where IAP monetization ` +
        `share is higher than ${refusal.requiredIapSharePercent ?? 50}%. We ` +
        `don't have an IAP share on record for this game yet — please reach ` +
        `out to your account manager to get it reviewed.`
      );
    case "event_name_required":
      return "Add your event name first";
    case "params_required":
      return "Add a challenge first";
    case "max_slots_reached":
      return `All ${refusal.maxSlots} challenge slots are in use.`;
    default:
      return fallback ?? "This optimization can't be activated right now.";
  }
};

const errorMessage = (
  error: unknown,
  rules?: OptimizeBoard["rules"]
): string => {
  if (error instanceof OptimizeApiError) {
    if (error.refusal) return refusalMessage(error.refusal, rules);
    // no machine-readable code: the service's own message beats a generic line
    if (error.serverMessage) return error.serverMessage;
  }
  return "Something went wrong — please try again.";
};

// A card with no stored row yet can come back with an empty `entries` array (the
// catalogue does exactly that), so treat "no entry" as an untouched slot rather
// than rendering nothing at all.
const emptyEntry = (slot = 0): BoardEntry => ({
  slot,
  status: "disabled",
  isEnabled: false,
  eventName: null,
  eventNameSource: "",
  activatedAt: null,
});

// the loaded board's rules for a game, so error copy can quote real numbers
const useBoardRules = (appId: string, platform: Platform) =>
  useOptimizeLive((s) => s.boards[`${platform}:${appId}`]?.rules);

// ------------------------------------------------------------ event name

const EventNameField: React.FC<{
  card: OptimizeCardDef;
  boardCard: BoardCard;
  entry: BoardEntry;
  appId: string;
  platform: Platform;
  disabled?: boolean;
}> = ({ card, boardCard, entry, appId, platform, disabled }) => {
  const saveCard = useOptimizeLive((s) => s.saveCard);
  const rules = useBoardRules(appId, platform);
  const form = card.eventForm ?? card.purchaseEventForm;
  const [name, setName] = useState(entry.eventName ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const textColor = useColorModeValue("secondaryGray.900", "white");

  useEffect(() => setName(entry.eventName ?? ""), [entry.eventName]);

  const dirty = name.trim() !== (entry.eventName ?? "");

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveCard(boardCard.key, {
        appId,
        platform,
        eventName: name.trim() || null,
      });
    } catch (err) {
      setError(errorMessage(err, rules));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Text color={textColor} fontWeight="bold" fontSize="sm" mb="6px">
        {form?.label ?? "Event name"}
      </Text>
      <Flex gap="10px" mb="8px">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && dirty && !disabled && save()}
          placeholder={form?.placeholder}
          isDisabled={disabled}
          borderRadius="12px"
          fontSize="sm"
        />
        <GamelightButton
          onClick={save}
          isDisabled={disabled || !dirty || saving}
          isLoading={saving}
          px="24px"
        >
          Save
        </GamelightButton>
      </Flex>
      <Text color={subColor} fontSize="xs">
        {form?.helper}
      </Text>
      {error && (
        <Text color="red.500" fontSize="sm" mt="8px">
          {error}
        </Text>
      )}
    </Box>
  );
};

// ------------------------------------------------------- activation controls

const ActivationControls: React.FC<{
  boardCard: BoardCard;
  entry: BoardEntry;
  game: OptimizeGame;
  appId: string;
  platform: Platform;
  slot?: number;
}> = ({ boardCard, entry, game, appId, platform, slot }) => {
  const saveCard = useOptimizeLive((s) => s.saveCard);
  const rules = useBoardRules(appId, platform);
  const app = useOptimizeLive((s) => s.boards[`${platform}:${appId}`]?.app);
  // an unmapped refusal on a purchase card + the ineligibility flag → the IAP
  // share rule is the reason, so say that instead of the generic line
  const refusalFallback = purchaseIneligible(boardCard.key, app)
    ? PURCHASE_INELIGIBLE_TEXT
    : undefined;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const activeBg = useColorModeValue("green.50", "whiteAlpha.100");
  const activeColor = useColorModeValue("green.600", "green.300");
  const { ConfirmDialog, openDialog } = useConfirmDialog();

  const setEnabled = async (isEnabled: boolean) => {
    setSaving(true);
    setError(null);
    try {
      await saveCard(boardCard.key, {
        appId,
        platform,
        ...(slot !== undefined ? { slot } : {}),
        isEnabled,
      });
    } catch (err) {
      // a 409 can still race in even though refusals disable the button
      setError(errorMessage(err, rules));
    } finally {
      setSaving(false);
    }
  };

  // activating a bonus challenge starts the per-app-and-platform cooldown, so
  // the partner confirms in a dialog before it goes live (Günay's feedback)
  const requestEnable = async () => {
    if (boardCard.group === "bonus_challenge") {
      const period = cooldownPeriodLabel(rules?.activationCooldownHours);
      const confirmed = await openDialog({
        title: "Confirm Challenge Activation",
        description:
          `Bonus challenges are limited to one activation every ${period} per app ` +
          `and platform. Once you activate this challenge, you will need to wait ` +
          `${period} before activating another bonus challenge for the same app ` +
          `and platform.`,
        confirmButtonText: "Confirm Activation",
      });
      if (!confirmed) return;
    }
    await setEnabled(true);
  };

  const refusal = entry.activationRefusal;
  const setupRefusal = refusal && SETUP_REFUSALS.includes(refusal.code);
  // the eligibility flag blocks on its own: the backend only sends a refusal
  // for purchase_challenges, not for multi-game purchase
  const ruleRefusalText =
    refusal && !setupRefusal
      ? refusalMessage(refusal, rules, refusalFallback)
      : refusalFallback;

  if (entry.status === "enabled") {
    return (
      <Box>
        <Flex
          bg={activeBg}
          color={activeColor}
          borderRadius="12px"
          p="12px 16px"
          align="center"
          mb="12px"
        >
          <Icon as={MdCheckCircle} w="18px" h="18px" mr="10px" />
          <Text fontSize="sm" fontWeight="600">
            Active since {entry.activatedAt ? `${utc(entry.activatedAt)} UTC` : "—"}{" "}
            for {game.name} ({game.platform}).
          </Text>
        </Flex>
        <Button
          leftIcon={<Icon as={MdPause} />}
          variant="outline"
          colorScheme="orange"
          borderRadius="12px"
          onClick={() => setEnabled(false)}
          isLoading={saving}
        >
          Pause
        </Button>
        {error && (
          <Text color="red.500" fontSize="sm" mt="10px">
            {error}
          </Text>
        )}
      </Box>
    );
  }

  return (
    <Box>
      {entry.status === "paused" && entry.activatedAt && (
        <Text color={subColor} fontSize="sm" mb="10px">
          Paused. Previously active since {utc(entry.activatedAt)} UTC for{" "}
          {game.name} ({game.platform}).
        </Text>
      )}
      <Tooltip
        label={setupRefusal ? refusalMessage(refusal!, rules) : ""}
        isDisabled={!setupRefusal}
      >
        <Box display="inline-block">
          <GamelightButton
            leftIcon={<Icon as={MdPlayArrow} />}
            onClick={requestEnable}
            isDisabled={Boolean(refusal) || Boolean(refusalFallback) || saving}
            isLoading={saving}
            px="32px"
          >
            {entry.status === "paused" ? "Resume" : "Activate"}
          </GamelightButton>
        </Box>
      </Tooltip>
      {ConfirmDialog}
      {ruleRefusalText && (
        <Text color="red.500" fontSize="sm" mt="10px">
          {ruleRefusalText}
        </Text>
      )}
      {error && (
        <Text color="red.500" fontSize="sm" mt="10px">
          {error}
        </Text>
      )}
    </Box>
  );
};

// ------------------------------------------------- time-limited challenge slots

// every field deliberately starts unset — per Günay nothing should arrive
// pre-marked (the "100" event-count prefill read as a suggestion)
const emptyForm = {
  eventName: "",
  eventType: "" as TimeLimitedParams["eventType"] | "",
  eventCount: "",
  taskName: "",
  timeLimitDays: "",
};

const SlotForm: React.FC<{
  boardCard: BoardCard;
  appId: string;
  platform: Platform;
  slot?: number; // present when editing an existing entry
  initial?: TimeLimitedParams;
  onDone: () => void;
}> = ({ boardCard, appId, platform, slot, initial, onDone }) => {
  const saveCard = useOptimizeLive((s) => s.saveCard);
  const rules = useBoardRules(appId, platform);
  const [form, setForm] = useState(
    initial
      ? {
          eventName: initial.eventName,
          eventType: initial.eventType as TimeLimitedParams["eventType"] | "",
          eventCount: initial.eventCount != null ? String(initial.eventCount) : "",
          taskName: initial.taskName,
          timeLimitDays: String(initial.timeLimitDays),
        }
      : emptyForm
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("secondaryGray.400", "whiteAlpha.300");

  const isRecurring = form.eventType === "recurring";
  const countValid = !isRecurring || Number(form.eventCount) > 0;
  const daysValid = Number.isInteger(Number(form.timeLimitDays)) && Number(form.timeLimitDays) > 0;
  const canSave = Boolean(
    form.eventName.trim() &&
      form.taskName.trim() &&
      form.eventType &&
      countValid &&
      daysValid
  );

  const field = (label: string, helper: string | undefined, input: React.ReactNode) => (
    <Box mb="10px">
      <Text color={textColor} fontWeight="600" fontSize="xs" mb="4px">
        {label}
      </Text>
      {input}
      {helper && (
        <Text color={subColor} fontSize="xs" mt="4px">
          {helper}
        </Text>
      )}
    </Box>
  );

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await saveCard(boardCard.key, {
        appId,
        platform,
        // omit slot when adding — the backend takes the first free slot;
        // send it when editing so the write can't land on the free slot
        ...(slot !== undefined ? { slot } : {}),
        params: {
          eventName: form.eventName.trim(),
          // canSave blocks the empty pre-selection state
          eventType: form.eventType as TimeLimitedParams["eventType"],
          // eventCount is required for recurring and rejected for unique
          ...(isRecurring ? { eventCount: Number(form.eventCount) } : {}),
          taskName: form.taskName.trim(),
          timeLimitDays: Number(form.timeLimitDays),
        },
      });
      onDone();
    } catch (err) {
      setError(errorMessage(err, rules));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box border="1px solid" borderColor={borderColor} borderRadius="12px" p="14px">
      <Text color={textColor} fontWeight="600" fontSize="sm" mb="10px">
        {slot !== undefined
          ? `Edit challenge (slot ${slot + 1})`
          : "New time-limited challenge"}
      </Text>
      {field(
        "Event Name",
        "Name should exactly match the name you send us through your MMP",
        <Input
          value={form.eventName}
          onChange={(e) => setForm({ ...form, eventName: e.target.value })}
          placeholder="e.g. level_completed"
          borderRadius="12px"
          fontSize="sm"
        />
      )}
      <Flex gap="10px">
        <Box flex="1">
          {field(
            "Event Type",
            undefined,
            <Select
              value={form.eventType}
              onChange={(e) =>
                setForm({
                  ...form,
                  eventType: e.target.value as TimeLimitedParams["eventType"] | "",
                })
              }
              placeholder="Select type"
              borderRadius="12px"
              fontSize="sm"
            >
              <option value="recurring">Recurring</option>
              <option value="unique">Unique</option>
            </Select>
          )}
        </Box>
        {isRecurring && (
          <Box flex="1">
            {field(
              "Event Count",
              "How many times should a user complete this event.",
              <Input
                value={form.eventCount}
                onChange={(e) => setForm({ ...form, eventCount: e.target.value })}
                type="number"
                min={1}
                placeholder="e.g. 100"
                borderRadius="12px"
                fontSize="sm"
              />
            )}
          </Box>
        )}
      </Flex>
      {field(
        "Task Name",
        'Example: "Complete 100 Levels"',
        <Input
          value={form.taskName}
          onChange={(e) => setForm({ ...form, taskName: e.target.value })}
          placeholder='e.g. "Complete 100 Levels"'
          borderRadius="12px"
          fontSize="sm"
        />
      )}
      {field(
        "Time limit (days)",
        "How many days a user have to complete the event. This should be a challenging task, and not where users naturally land.",
        <Input
          value={form.timeLimitDays}
          onChange={(e) => setForm({ ...form, timeLimitDays: e.target.value })}
          type="number"
          min={1}
          placeholder="e.g. 7"
          borderRadius="12px"
          fontSize="sm"
          maxW="160px"
        />
      )}
      {error && (
        <Text color="red.500" fontSize="sm" mb="8px">
          {error}
        </Text>
      )}
      <Flex gap="10px" align="center" mt="8px">
        <GamelightButton onClick={save} isDisabled={!canSave || saving} isLoading={saving}>
          Save
        </GamelightButton>
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </Flex>
    </Box>
  );
};

const SlotsSection: React.FC<{
  boardCard: BoardCard;
  game: OptimizeGame;
  appId: string;
  platform: Platform;
}> = ({ boardCard, game, appId, platform }) => {
  const [addOpen, setAddOpen] = useState(false);
  const [editSlot, setEditSlot] = useState<number | null>(null);
  const rules = useBoardRules(appId, platform);
  const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const rowBg = useColorModeValue("secondaryGray.100", "whiteAlpha.50");
  const dashedColor = useColorModeValue("secondaryGray.400", "whiteAlpha.300");

  const filled = boardCard.entries.filter((entry) => entry.params);
  const hasFreeSlot = filled.length < boardCard.maxSlots;

  return (
    <Box>
      <Flex align="center" justify="space-between" mb="10px">
        <Text color={textColor} fontWeight="bold" fontSize="sm">
          Challenges for this title
        </Text>
        <Badge
          colorScheme={hasFreeSlot ? "gray" : "green"}
          borderRadius="full"
          px="8px"
        >
          {filled.length}/{boardCard.maxSlots} added
        </Badge>
      </Flex>

      {filled.map((entry) =>
        editSlot === entry.slot ? (
          <Box key={entry.slot} mb="8px">
            <SlotForm
              boardCard={boardCard}
              appId={appId}
              platform={platform}
              slot={entry.slot}
              initial={entry.params}
              onDone={() => setEditSlot(null)}
            />
          </Box>
        ) : (
          <Box
            key={entry.slot}
            bg={rowBg}
            borderRadius="12px"
            p="10px 14px"
            mb="8px"
          >
            <Flex align="center">
              <Icon
                as={MdCheckCircle}
                color={entry.status === "enabled" ? "green.500" : "gray.400"}
                w="18px"
                h="18px"
                mr="10px"
              />
              <Box flex="1">
                <Text color={textColor} fontSize="sm" fontWeight="600">
                  {entry.params!.taskName}
                </Text>
                <Text color={subColor} fontSize="xs">
                  {entry.params!.eventName} · {entry.params!.eventType}
                  {entry.params!.eventCount
                    ? ` ×${entry.params!.eventCount}`
                    : ""}{" "}
                  · Within {entry.params!.timeLimitDays} days · {game.platform}
                </Text>
              </Box>
              {/* a saved-but-disabled slot is a challenge one click away from
                  being missed — flag it loudly instead of a grey "Off"
                  (Günay almost missed the activation step) */}
              <Badge
                colorScheme={
                  entry.status === "enabled"
                    ? "green"
                    : entry.status === "paused"
                    ? "orange"
                    : "red"
                }
                borderRadius="full"
                px="8px"
                mr="8px"
              >
                {entry.status === "enabled"
                  ? "Active"
                  : entry.status === "paused"
                  ? "Paused"
                  : "Needs activation"}
              </Badge>
              {/* Rewriting a live challenge is cooldown-gated by the backend
                  (a param swap is a new challenge in disguise); `editRefusal`
                  says in advance whether the PUT would be refused, so the icon
                  only locks when it actually would (CONTRACT.md). */}
              <Tooltip
                label={
                  entry.editRefusal
                    ? refusalMessage(entry.editRefusal, rules)
                    : ""
                }
                isDisabled={!entry.editRefusal}
              >
                <Box as="span" display="inline-flex">
                  <Icon
                    as={MdEdit}
                    color={entry.editRefusal ? "gray.400" : subColor}
                    w="16px"
                    h="16px"
                    cursor={entry.editRefusal ? "not-allowed" : "pointer"}
                    onClick={
                      entry.editRefusal
                        ? undefined
                        : () => setEditSlot(entry.slot)
                    }
                  />
                </Box>
              </Tooltip>
            </Flex>
            <Box mt="10px">
              <ActivationControls
                boardCard={boardCard}
                entry={entry}
                game={game}
                appId={appId}
                platform={platform}
                slot={entry.slot}
              />
            </Box>
          </Box>
        )
      )}

      {hasFreeSlot && !addOpen && (
        <Flex
          border="1px dashed"
          borderColor={dashedColor}
          borderRadius="12px"
          p="12px"
          align="center"
          justify="center"
          cursor="pointer"
          onClick={() => setAddOpen(true)}
          _hover={{ borderColor: "brand.400", color: "brand.400" }}
          color={subColor}
        >
          <Icon as={MdInfoOutline} w="16px" h="16px" mr="6px" />
          <Text fontSize="sm">
            {filled.length === 0 ? "Add a challenge" : "Add another challenge"}
          </Text>
        </Flex>
      )}

      {addOpen && (
        <SlotForm
          boardCard={boardCard}
          appId={appId}
          platform={platform}
          onDone={() => setAddOpen(false)}
        />
      )}

      {!hasFreeSlot && (
        <Text color={subColor} fontSize="xs" mt="8px">
          You have used all {boardCard.maxSlots} time-limited challenge slots
          for this title.
        </Text>
      )}
    </Box>
  );
};

// ------------------------------------------------------------- creatives

const CreativesSection: React.FC<{
  appId: string;
  platform: Platform;
}> = ({ appId, platform }) => {
  const gameId = `${platform}:${appId}`;
  // shared with the preview via the live store — one fetch serves both
  const { creatives, failed } = useCreatives(gameId);
  const loadCreatives = useOptimizeLive((s) => s.loadCreatives);
  const [language, setLanguage] = useState(CREATIVE_LANGUAGE_CODES[0].code);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const rowBg = useColorModeValue("secondaryGray.100", "whiteAlpha.50");

  useEffect(() => {
    // force: the urls are ~1-hour presigned links, so every open of this card
    // fetches them fresh instead of trusting the store's per-visit cache
    loadCreatives(appId, platform, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, platform]);

  const refresh = () => loadCreatives(appId, platform, true);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    // backend only checks content type + size; specs are validated here
    const invalid = await validateCreativeFile(file);
    if (invalid) {
      setError(invalid.message);
      return;
    }
    setBusy(true);
    try {
      await uploadCreative(language, appId, platform, file);
      await refresh();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id: number) => {
    setBusy(true);
    try {
      await deleteCreative(id);
      await refresh();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // codes dropped from the picker only stop new uploads — creatives already
  // filed under one stay listed and deletable (CONTRACT.md), with a real label
  const LEGACY_LANGUAGE_LABELS: Record<string, string> = { tr: "Turkish" };
  const languageLabel = (code: string) =>
    CREATIVE_LANGUAGE_CODES.find((l) => l.code === code)?.label ??
    LEGACY_LANGUAGE_LABELS[code] ??
    code;

  return (
    <Box>
      <Text color={textColor} fontWeight="bold" fontSize="sm" mb="6px">
        Upload creatives
      </Text>
      <Flex gap="10px" mb="8px">
        <Select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          borderRadius="12px"
          fontSize="sm"
          maxW="180px"
        >
          {CREATIVE_LANGUAGE_CODES.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.label}
            </option>
          ))}
        </Select>
        <GamelightButton
          leftIcon={<Icon as={MdUploadFile} />}
          onClick={() => fileRef.current?.click()}
          isLoading={busy}
        >
          Upload
        </GamelightButton>
        <input
          ref={fileRef}
          type="file"
          accept="video/mp4,video/quicktime,image/jpeg,image/png"
          style={{ display: "none" }}
          onChange={(e) => onFile(e.target.files?.[0])}
        />
      </Flex>
      <Text color={subColor} fontSize="xs" mb="12px">
        One video and one banner per language — uploading again replaces the
        existing one of the same kind; the video plays when present, otherwise
        the banner shows. Video (MP4/MOV, ideally 15–30 s) or image (JPG/PNG),
        16:9 landscape, min 1280×720.
      </Text>
      {error && (
        <Text color="red.500" fontSize="sm" mb="8px">
          {error}
        </Text>
      )}

      {creatives === undefined ? (
        failed ? (
          <Text color={subColor} fontSize="sm">
            Couldn't load this title's creatives.{" "}
            <Text
              as="span"
              color="brand.400"
              cursor="pointer"
              onClick={refresh}
            >
              Retry
            </Text>
          </Text>
        ) : (
          <Spinner size="sm" />
        )
      ) : creatives.length === 0 ? (
        // an empty array is the normal starting state, not an error — the
        // store-listing creative keeps showing until something is uploaded
        <Text color={subColor} fontSize="sm">
          No overrides uploaded yet — the store creative is shown for every
          language.
        </Text>
      ) : (
        creatives.map((creative) => (
          <Flex
            key={creative.id}
            bg={rowBg}
            borderRadius="12px"
            p="10px 14px"
            align="center"
            mb="8px"
          >
            <Icon as={MdCheckCircle} color="green.500" w="18px" h="18px" mr="10px" />
            <Box flex="1">
              <Text
                as="a"
                href={creative.url}
                target="_blank"
                rel="noreferrer"
                color={textColor}
                fontSize="sm"
                fontWeight="600"
                _hover={{ textDecoration: "underline" }}
              >
                {creative.fileName}
              </Text>
              <Text color={subColor} fontSize="xs">
                {languageLabel(creative.languageCode)} ·{" "}
                {creative.type === "video" ? "Video" : "Banner"} ·{" "}
                {(creative.fileSize / (1024 * 1024)).toFixed(1)} MB
                {creative.updatedAt
                  ? ` · Updated ${utc(creative.updatedAt)} UTC`
                  : ""}
              </Text>
            </Box>
            <Badge colorScheme="green" borderRadius="full" px="8px">
              Live
            </Badge>
            <Icon
              as={MdDelete}
              color={subColor}
              w="16px"
              h="16px"
              ml="10px"
              cursor="pointer"
              onClick={() => remove(creative.id)}
            />
          </Flex>
        ))
      )}
    </Box>
  );
};

// ---------------------------------------------------------------- main panel

const GameSetup: React.FC<{
  card: OptimizeCardDef;
  game: OptimizeGame;
}> = ({ card, game }) => {
  const board = useOptimizeLive((s) => s.boards[game.id]);
  const boardLoading = useOptimizeLive((s) => s.boardLoading);
  const loadBoard = useOptimizeLive((s) => s.loadBoard);
  const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");

  const parsed = parseGameId(game.id);

  useEffect(() => {
    if (parsed) loadBoard(parsed.appId, parsed.platform);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.id]);

  if (!parsed) return null;
  const { appId, platform } = parsed;

  if (!board) {
    return (
      <Flex align="center" gap="10px" py="10px">
        {boardLoading === game.id ? (
          <>
            <Spinner size="sm" />
            <Text color={subColor} fontSize="sm">
              Loading setup…
            </Text>
          </>
        ) : (
          <Text color={subColor} fontSize="sm">
            Couldn't load this title's setup.{" "}
            <Text
              as="span"
              color="brand.400"
              cursor="pointer"
              onClick={() => loadBoard(appId, platform)}
            >
              Retry
            </Text>
          </Text>
        )}
      </Flex>
    );
  }

  const boardCard = board.cards.find(
    (c) => c.key === (card.id as OptimizationKey)
  );
  if (!boardCard) {
    return (
      <Text color={subColor} fontSize="sm" py="10px">
        This optimization isn't available for this title.
      </Text>
    );
  }

  const entry = boardCard.entries[0] ?? emptyEntry();
  const isTimeLimited = boardCard.maxSlots > 1;
  const isCreatives = boardCard.key === "override_creatives";
  const showEventName =
    !isCreatives && boardCard.requiresEventName && Boolean(entry);

  return (
    <Flex direction="column" gap="20px">
      {isCreatives && <CreativesSection appId={appId} platform={platform} />}

      {showEventName && (
        <EventNameField
          card={card}
          boardCard={boardCard}
          entry={entry}
          appId={appId}
          platform={platform}
          disabled={purchaseIneligible(boardCard.key, board.app)}
        />
      )}

      {isTimeLimited ? (
        <SlotsSection
          boardCard={boardCard}
          game={game}
          appId={appId}
          platform={platform}
        />
      ) : (
        !isCreatives &&
        entry && (
          <Box>
            {/* only a separator when the event-name field sits above it —
                otherwise it doubles up with the panel's own divider */}
            {showEventName && <Divider mb="16px" />}
            <ActivationControls
              boardCard={boardCard}
              entry={entry}
              game={game}
              appId={appId}
              platform={platform}
            />
          </Box>
        )
      )}
    </Flex>
  );
};

export default GameSetup;
